import AuthActions from './actions-types'
import AuthMutations from './mutations-types'

import AppActions from '@/store/app/actions-types.js'

import api from '@/services/api'
import jwt from '@/services/jwt'

let saveLoginDataPromise = null
let saveLoginDataToken = null

// Single-flight guard for token renewal. The renew endpoint rotates tokens
// (issuing a new one invalidates the previous), so two concurrent renewals
// would send the same soon-to-be-invalidated token; the loser gets rejected
// and triggers a logout/redirect to login. Sharing one in-flight promise keyed
// by the token being renewed prevents that stale-token race.
let renewTokenPromise = null
let renewTokenToken = null

const isCurrentSaveLoginData = token => saveLoginDataToken === token

export default {
  [AuthActions.DO_LOGIN]({ dispatch }, credentials) {
    return new Promise((resolve, reject) => {

      api.core.auth
        .login(credentials)
        .then((response) => {
          dispatch(AuthActions.SAVE_LOGIN_DATA, response.data.token)
            .then(() => {
              resolve()
            })
            .catch((error) => reject(error))
        })
        .catch((error) => {
          reject(error.response.data);
        })
    })
  },

  [AuthActions.RENEW_TOKEN]({ state, dispatch }) {
    const token = state.token

    // Reuse the in-flight renewal for the same token so parallel callers
    // (e.g. multiple axios interceptors firing on a near-expiry token) don't
    // each send a renew request with the same token and race the rotation.
    if (renewTokenPromise && renewTokenToken === token) {
      return renewTokenPromise
    }

    renewTokenToken = token
    renewTokenPromise = new Promise((resolve, reject) => {
      api.core.auth
        .renewToken({ token })
        .then((response) => {
          dispatch(AuthActions.SAVE_LOGIN_DATA, response.data.token)
            .then(() => {
              resolve()
            })
            .catch((error) => reject(error))
        })
        .catch((error) => {
          reject(error.response ? error.response.data : error);
        })
    })
      .finally(() => {
        if (renewTokenToken === token) {
          renewTokenPromise = null
          renewTokenToken = null
        }
      })

    return renewTokenPromise
  },

  [AuthActions.DO_LOGOUT]({ dispatch }) {
    return new Promise((resolve) => {
      saveLoginDataPromise = null
      saveLoginDataToken = null
      renewTokenPromise = null
      renewTokenToken = null

      dispatch(AuthActions.RESET_LOGIN_DATA)

      resolve()
    })
  },

  [AuthActions.SAVE_LOGIN_DATA]({ commit, dispatch }, token) {
    if (saveLoginDataPromise && saveLoginDataToken === token) {
      return saveLoginDataPromise
    }

    saveLoginDataToken = token
    saveLoginDataPromise = jwt
      .decode(token)
      .then((decoded) => {
        if (!isCurrentSaveLoginData(token)) {
          return
        }

        commit(AuthMutations.SET_TOKEN, token)
        commit(AuthMutations.SET_USER_DATA, decoded.data)
        commit(AuthMutations.SET_USER_ROLES)

        return dispatch(`app/${AppActions.GET_AND_SET_USER_APPLICATIONS}`, {}, {root:true})
      })
      .then(() => {
        if (isCurrentSaveLoginData(token)) {
          commit(AuthMutations.SET_AUTHENTICATED, true)
        }
      })
      .catch((error) => {
        // @TODO: DISPATCH ALERT
        if (isCurrentSaveLoginData(token)) {
          dispatch(AuthActions.RESET_LOGIN_DATA)
        }

        throw error
      })
      .finally(() => {
        if (isCurrentSaveLoginData(token)) {
          saveLoginDataPromise = null
          saveLoginDataToken = null
        }
      })

    return saveLoginDataPromise
  },

  [AuthActions.RESET_LOGIN_DATA]({ commit }) {
    commit(AuthMutations.SET_AUTHENTICATED, false)
    commit(AuthMutations.SET_TOKEN, null)
    commit(AuthMutations.SET_USER_DATA, {})
  },

  async [AuthActions.CHECK_TOKEN]({ dispatch, state }) {
    // Wait out any in-flight login/renew processing before reporting auth
    // status. A concurrent RENEW_TOKEN (triggered by axios interceptors when
    // the token is near expiry) can supersede an older SAVE_LOGIN_DATA call
    // with a newer token. Looping here guarantees we resolve only once the
    // most recent processing has fully settled the state (authenticated flag
    // and userApplications), instead of resolving on a half-initialized
    // superseded chain, which caused spurious redirects to the home page.
    while (saveLoginDataPromise) {
      const pending = saveLoginDataPromise
      await pending

      // If the promise we just awaited is still the current one, its `finally`
      // has cleared it and we are done. If a newer one was started while we
      // awaited, loop again to await that one too.
      if (saveLoginDataPromise === pending) {
        break
      }
    }

    if (state.authenticated) {
      await dispatch(`app/${AppActions.GET_AND_SET_USER_APPLICATIONS}`, {}, {root:true})
      return
    }

    const token = localStorage.getItem('token')

    if (!token) {
      return
    }

    await dispatch(AuthActions.SAVE_LOGIN_DATA, token)
  },

  // localStorage is shared by tabs while Vuex state is not. Adopt a newer
  // token written by another tab before making authenticated requests.
  async [AuthActions.SYNC_STORED_TOKEN]({ dispatch, state }) {
    const storedToken = localStorage.getItem('token')

    if (!storedToken || storedToken === state.token) {
      return false
    }

    await dispatch(AuthActions.SAVE_LOGIN_DATA, storedToken)
    return true
  },

  [AuthActions.RESET_PASSWORD]({}, params) {
    return new Promise((resolve, reject) => {
      api.core.auth
        .resetPasswd( params )
        .then(() => {
          resolve()
        })
        .catch((error) => {
          reject(error.response.data);
        })
    })
  },
}
